import { useCallback, useEffect, useMemo, useState } from "react";
import _ from "lodash";

import { colors } from "theme/palette";
import { handleInvalidRequest } from "utils/sdk";

import Button from "components/Button";
import Dialog, {
  DialogActions,
  DialogContent,
  DialogTitle,
} from "components/Dialog";
import Paper from "components/Paper";
import Select from "components/Select";
import Skeleton from "components/Skeleton";
import Text from "components/Text";
import toast from "components/Toast";

import { IMemoRisk, riskAdd, useMemoRiskTemplates } from "entities/Memo/sdk";

export interface IRiskAdditionDialogButton {
  dealId: number;
  memoSectionId: number;
  onUpdate: () => void;
  createCallback: ({ riskData }: { riskData: IMemoRisk }) => void;
}

const RiskAdditionDialogButton = ({
  dealId,
  onUpdate,
  memoSectionId,
  createCallback,
}: IRiskAdditionDialogButton) => {
  const [isAddRiskDialogOpen, setIsAddRiskDialogOpen] = useState(false);
  const [value, setValue] = useState<string>("");

  const { data: riskTemplates, isLoading: isFetchingRiskTemplates } =
    useMemoRiskTemplates({
      dealId,
      enabled: isAddRiskDialogOpen,
    });

  useEffect(() => {
    if (!isAddRiskDialogOpen) {
      setValue("");
    }
  }, [isAddRiskDialogOpen]);

  const riskTemplateOptions = useMemo(() => {
    if (isFetchingRiskTemplates) {
      return [];
    }

    return [
      ...(riskTemplates?.map((template) => ({
        label: template.title,
        value: template.id.toString(),
      })) || []),
      {
        label: "Other",
        value: "other",
      },
    ];
  }, [riskTemplates, isFetchingRiskTemplates]);

  const handleSave = useCallback(() => {
    riskAdd({
      memoSectionId,
      dealId,
      riskTemplateId: value === "other" ? undefined : parseInt(value),
    })
      .then((riskData) => {
        createCallback({ riskData });
        setIsAddRiskDialogOpen(false);
        onUpdate();
        toast.successMessage("Your item has been created successfully!");
      })
      .catch(handleInvalidRequest);
  }, [value, memoSectionId, dealId, onUpdate, createCallback]);

  return (
    <>
      <Button size="small" onClick={() => setIsAddRiskDialogOpen(true)}>
        + Add new item
      </Button>

      <Dialog
        fullWidth
        maxWidth="sm"
        open={isAddRiskDialogOpen}
        onClose={() => setIsAddRiskDialogOpen(false)}
      >
        <DialogTitle sx={{ padding: 2 }}>
          <Paper sx={{ backgroundColor: colors.blue10, borderRadius: "4px" }}>
            <Text variant="h2">+ Add new item</Text>
          </Paper>
        </DialogTitle>
        <DialogContent>
          <Text variant="text3" fontWeight={500}>
            Item type
          </Text>
          <Text variant="text4" color={colors.gray80} marginBottom={0.5}>
            Choose an item from the dropdown list
          </Text>

          {_.isEmpty(riskTemplateOptions) ? (
            <Skeleton height={56} />
          ) : (
            <Select
              fullWidth
              options={riskTemplateOptions}
              onChange={(e) => setValue(e.target.value)}
              value={value}
            />
          )}
        </DialogContent>
        <DialogActions sx={{ padding: 2 }}>
          <Button variant="text" onClick={() => setIsAddRiskDialogOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={_.isEmpty(value)}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default RiskAdditionDialogButton;
